import { type Car } from '@prisma/client'
import { useEffect, useState } from 'react'
import { formatPrice } from '#app/shared/lib/price.ts'
import { Chip } from '#app/shared/ui/data-display/Chip.js'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import { Icon } from '#app/shared/ui/icons/Icon.js'
import {
  WarrantyDurationEnum,
  WarrantyTranslationEnum,
  WarrantyTypeEnum,
  type WarrantyDurationTypes,
  type WarrantyTypes,
} from '../../../../../types/enums'

const warrantyText = {
  'Basic Retail': [
    'Sin límite de kilómetros',
    'Coche de sustitución con el Pack Óptima',
    'Motor',
    'Caja de cambios',
    'Sistema de refrigeración',
    'Sistema eléctrico esencial',
  ],
  'Advanced Retail': [
    'Sin límite de kilómetros',
    'Coche de sustitución con el Pack Óptima',
    'Motor',
    'Caja de cambios',
    'Sistema de refrigeración ampliado',
    'Sistema eléctrico y electrónico avanzado',
    'Sistemas de sobrealimentación (turbo)',
    'Sistemas de control de emisiones',
  ],
}

type Props = {
  warrantyChoice: {
    type: WarrantyTypes
    duration: WarrantyDurationTypes
  }
  setWarrantyChoice: (warrantyChoice: {
    type: WarrantyTypes
    duration: WarrantyDurationTypes
  }) => void
  car: Car
  activeNumber?: number
  hasPackOptima?: boolean
}

export const warrantyPrices = (duration: number) => {
  switch (duration) {
    case WarrantyDurationEnum.ONE_YEAR:
      return {
        basicPrice: 0,
        advancedPrice: 490,
      }
    case WarrantyDurationEnum.TWO_YEARS:
      return {
        basicPrice: 200,
        advancedPrice: 980,
      }
    case WarrantyDurationEnum.THREE_YEARS:
      return {
        basicPrice: 380,
        advancedPrice: 1470,
      }
    default:
      return {
        basicPrice: 0,
        advancedPrice: 490,
      }
  }
}
export const warrantyPriceByWarrantyType = ({
  type,
  duration,
  hasPackOptima,
}: {
  type: WarrantyTypes
  duration: WarrantyDurationTypes
  hasPackOptima: boolean
}) => {
  // Pack Óptima benefit: Free basic warranty for 2 years
  if (type === 'Basic Retail' && duration > 1 && hasPackOptima) {
    // second year free, so we rest 1 year
    const { basicPrice } = warrantyPrices(duration - 1)
    return basicPrice
  }

  const warrantyPrice = warrantyPrices(duration)

  return type === 'Basic Retail'
    ? warrantyPrice.basicPrice
    : warrantyPrice.advancedPrice
}

export default function Warranty({
  warrantyChoice,
  setWarrantyChoice,
  hasPackOptima = false,
}: Props) {
  const [basicPrice, setBasicPrice] = useState(0)
  const [advancedPrice, setAdvancedPrice] = useState(270)
  const isPackGama = hasPackOptima && warrantyChoice.duration > 1

  useEffect(() => {
    const { basicPrice, advancedPrice } = warrantyPrices(
      warrantyChoice.duration,
    )
    // Apply Pack Óptima benefit: Free basic warranty for 2 years
    if (hasPackOptima && warrantyChoice.duration > 1) {
      const { basicPrice } = warrantyPrices(warrantyChoice.duration - 1)
      setBasicPrice(basicPrice)
    } else {
      setBasicPrice(basicPrice)
    }

    setAdvancedPrice(advancedPrice)
  }, [warrantyChoice.duration, hasPackOptima])

  return (
    <>
      <p className="mb-5 mt-8 text-base-medium text-neutral-500">
        Elige la garantía que mejor se ajuste a tu situación.
      </p>
      <div>
        <div className="mb-4 flex w-full flex-col space-y-3">
          <div
            className={`
              z-[1] flex max-w-full flex-grow flex-wrap items-center gap-2

              md:flex-nowrap
            `}
          >
            {Object.values(WarrantyDurationEnum).map((warranty) => (
              <button
                type="button"
                key={warranty}
                className={`
                  flex h-11 min-h-11 w-[70px] min-w-[70px] items-center
                  justify-center rounded-lg bg-white text-neutral-500

                  ${
                    warrantyChoice.duration === warranty
                      ? 'border-2 border-primary-500'
                      : `border border-neutral-100`
                  }
                `}
                onClick={() => {
                  setWarrantyChoice({
                    ...warrantyChoice,
                    duration: warranty,
                  })
                }}
              >
                {warranty} año{warranty === 1 ? '' : 's'}
              </button>
            ))}
          </div>
        </div>
        <div
          className={`
            flex max-w-[670px] flex-col flex-wrap gap-3

            md:flex-row

            xl:grid xl:grid-cols-2
          `}
        >
          {/* 1 */}
          <button
            type="button"
            className={`
              relative flex min-h-[151px] w-full cursor-pointer flex-col
              items-start justify-start rounded-base bg-white p-6

              md:min-h-[217px] md:w-[327px]

              ${
                warrantyChoice.type === WarrantyTypeEnum.BASIC
                  ? `border-2 border-primary-500`
                  : `border border-neutral-75`
              }
            `}
            onClick={() =>
              setWarrantyChoice({
                ...warrantyChoice,
                type: WarrantyTypeEnum.BASIC,
              })
            }
          >
            <div
              className={`
                absolute right-3 top-3 h-6 w-6 items-center justify-center
                rounded-full border border-primary-500

                ${
                  warrantyChoice.type === WarrantyTypeEnum.BASIC
                    ? `flex`
                    : `hidden`
                }
              `}
            >
              <Icon
                name="basic/check"
                className="text-primary-500"
                size="12px"
              />
            </div>
            <div className="flex items-center gap-3">
              <p className="mb-2 text-base-bold text-neutral-900">
                {WarrantyTranslationEnum['Basic Retail']}
              </p>
            </div>
            <p className="mb-3 text-h5-bold text-neutral-900">
              {formatPrice(basicPrice)}
            </p>
            {isPackGama ? (
              <Chip
                className={`
                  !h-[20px] !min-h-[25px] text-nowrap rounded-[2px]

                  ${
                    warrantyChoice.type === WarrantyTypeEnum.BASIC
                      ? `bg-green-600`
                      : `bg-green-500`
                  }
                `}
              >
                <p className="text-subtitle1-bold text-white">
                  {warrantyChoice.duration === WarrantyDurationEnum.TWO_YEARS
                    ? '¡GRATIS CON PACK ÓPTIMA!'
                    : '¡MÁS ELEGIDA!'}
                </p>
              </Chip>
            ) : (
              <Chip
                className={`
                  !h-[20px] !min-h-[25px] w-[135px] text-nowrap rounded-[2px]

                  ${
                    warrantyChoice.type === WarrantyTypeEnum.BASIC
                      ? `bg-primary-600`
                      : `bg-[#01A8DD]`
                  }
                `}
              >
                <p className="text-subtitle1-bold text-white">
                  1er AÑO INCLUIDA
                </p>
              </Chip>
            )}
            <Spacer size="20px" />
            <div className="flex flex-col gap-3">
              {warrantyText['Basic Retail'].map((text) => {
                const shouldStrike =
                  !hasPackOptima && text.includes('Pack Óptima')
                return (
                  <div
                    key={text}
                    className={`flex min-h-2.5 items-start gap-1`}
                  >
                    <Icon
                      name="basic/check"
                      className="text-primary-500"
                      size="10px"
                    />
                    {/* <Icon name="basic/x" color="#ff3b30" size="10px" /> */}
                    <span
                      className={`
                        text-start text-subtitle1-medium text-neutral-500

                        ${shouldStrike ? 'line-through decoration-2' : ''}
                      `}
                    >
                      {text}
                    </span>
                  </div>
                )
              })}
            </div>
          </button>

          {/* 2 */}

          <button
            type="button"
            className={`
              relative flex min-h-[151px] w-full cursor-pointer flex-col
              items-start justify-start rounded-base bg-white p-6

              md:min-h-[217px] md:w-[327px]

              ${
                warrantyChoice.type === WarrantyTypeEnum.ADVANCED
                  ? `border-2 border-primary-500`
                  : `border border-neutral-75`
              }
            `}
            onClick={() =>
              setWarrantyChoice({
                ...warrantyChoice,
                type: WarrantyTypeEnum.ADVANCED,
              })
            }
          >
            <div
              className={`
                absolute right-3 top-3 h-6 w-6 items-center justify-center
                rounded-full border border-primary-500

                ${
                  warrantyChoice.type === WarrantyTypeEnum.ADVANCED
                    ? `flex`
                    : `hidden`
                }
              `}
            >
              <Icon
                name="basic/check"
                className="text-primary-500"
                size="12px"
              />
            </div>

            <div className="flex items-center gap-3">
              <p className="mb-2 text-base-bold text-neutral-900">
                {WarrantyTranslationEnum['Advanced Retail']}
              </p>
            </div>
            <p className="mb-3 text-h5-bold text-neutral-900">
              {formatPrice(advancedPrice)}
            </p>

            <Chip
              className={`
                !h-[20px] !min-h-[25px] w-[118px] text-nowrap rounded-[2px]

                ${
                  warrantyChoice.type === WarrantyTypeEnum.ADVANCED
                    ? `bg-primary-600`
                    : `bg-[#01A8DD]`
                }
              `}
            >
              <p className="text-subtitle1-bold text-white">MEJOR OPCIÓN</p>
            </Chip>
            <Spacer size="20px" />
            <div className="flex flex-col gap-3">
              {warrantyText['Advanced Retail'].map((text) => (
                <div key={text} className={`flex min-h-2.5 items-start gap-1`}>
                  <Icon
                    name="basic/check"
                    className="text-primary-500"
                    size="10px"
                  />
                  <span
                    className={`
                      text-start text-subtitle1-medium text-neutral-900
                    `}
                  >
                    {text}
                  </span>
                </div>
              ))}
            </div>
          </button>
        </div>
      </div>
    </>
  )
}
